import MarketingImage from '@/components/landing/MarketingImage';
import { detachPageSeo } from '@/utils/pageSeo';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Brain,
  BookOpenText,
  ChartNoAxesCombined,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ClipboardList,
  Database,
  Dna,
  ExternalLink,
  FlaskConical,
  HeartPulse,
  Microscope,
  PlayCircle,
  Rows3,
  Salad,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Users,
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
const mealPlanScreen = '/images/marketing/landing-feature-screens-meal-plan.webp';
const brandMarkLogo = '/images/marketing/lovable-uploads-cc1c4082-b52d-4f93-9c6a-0bc389e4be79.webp';
import {
  PNP_DEMO_VIDEO_URL,
  PNP_FAQS,
  PNP_LAST_REVIEWED_DISPLAY,
  PNP_META_DESCRIPTION,
  PNP_PAGE_CANONICAL_URL,
  PNP_PAGE_TITLE,
  PNP_REVIEWED_BY,
  PNP_WORKFLOW_IMAGE_DESKTOP,
  PNP_WORKFLOW_IMAGE_MOBILE,
  SITE_URL,
} from '@/data/personalisedNutritionSeo';
import styles from './PersonalisedNutritionPage.module.css';

const createMeta = (attributes: Record<string, string>) => {
  const element = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-pnp-seo', 'true');
  document.head.appendChild(element);
};

const createLink = (attributes: Record<string, string>) => {
  const element = document.createElement('link');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-pnp-seo', 'true');
  document.head.appendChild(element);
};

const createJsonLd = (value: Record<string, unknown>) => {
  const element = document.createElement('script');
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(value);
  element.setAttribute('data-pnp-seo', 'true');
  document.head.appendChild(element);
};

const usePnpPageSeo = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const restorePageSeo = detachPageSeo();
    document.querySelectorAll('[data-pnp-seo="true"]').forEach(element => element.remove());

    document.title = PNP_PAGE_TITLE;
    createMeta({ name: 'description', content: PNP_META_DESCRIPTION });
    createMeta({ property: 'og:title', content: PNP_PAGE_TITLE });
    createMeta({ property: 'og:description', content: PNP_META_DESCRIPTION });
    createMeta({ property: 'og:url', content: PNP_PAGE_CANONICAL_URL });
    createMeta({ property: 'og:type', content: 'website' });
    createMeta({ property: 'og:site_name', content: 'CalorieScience' });
    createMeta({ name: 'twitter:card', content: 'summary_large_image' });
    createMeta({ name: 'twitter:title', content: PNP_PAGE_TITLE });
    createMeta({ name: 'twitter:description', content: PNP_META_DESCRIPTION });
    createLink({ rel: 'canonical', href: PNP_PAGE_CANONICAL_URL });
    createLink({ rel: 'alternate', hreflang: 'en-GB', href: PNP_PAGE_CANONICAL_URL });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'CalorieScience Personalised Nutrition Platform',
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      description:
        'An AI-assisted personalised nutrition platform for UK practitioners — connect biomarkers, genetics and lifestyle data to build evidence-based, personalised meal plans.',
      url: PNP_PAGE_CANONICAL_URL,
      provider: {
        '@type': 'Organization',
        name: 'CalorieScience',
        url: SITE_URL,
      },
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: PNP_FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Personalised Nutrition Platform', item: PNP_PAGE_CANONICAL_URL },
      ],
    });

    return () => {
      document.querySelectorAll('[data-pnp-seo="true"]').forEach(element => element.remove());
      restorePageSeo();
      document.title = previousTitle;
    };
  }, []);
};

const STAT_CARDS = [
  {
    stat: '800 participants, 46,898 meals',
    text: 'found high variability in glucose response to identical foods',
    source: 'Zeevi et al., Cell, 2015',
    href: 'https://www.cell.com/fulltext/S0092-8674(15)01481-6',
  },
  {
    stat: '7 RCTs, n = 873 (prediabetes & metabolic syndrome)',
    text: 'found precision nutrition interventions consistently improved HbA1c and waist circumference, with mixed results on lipids and blood pressure',
    source: 'Robertson et al., Nutrients, 2024',
    href: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC11124316/',
  },
  {
    stat: '3,500+ BANT-registered UK practitioners',
    text: 'applying personalised nutrition in clinical practice',
    source: 'BANT Practitioner Register',
    href: 'https://practitioner-search.bant.org.uk/',
  },
];

const GENERIC_PLAN_INSIGHTS = [
  {
    icon: CircleAlert,
    title: 'Population guidance misses individual response',
    text:
      'Standard guidelines matter for public health, but they do not explain why two clients can follow the same healthy plan and get very different results.',
  },
  {
    icon: ChartNoAxesCombined,
    title: 'Personalised advice changes behaviour',
    text:
      'Food4Me showed personalised online nutrition advice led to more appropriate dietary changes than generic population guidance.',
  },
  {
    icon: HeartPulse,
    title: 'The same meal can land differently',
    text:
      'Postprandial glucose studies show substantial variation in blood sugar responses to identical meals, even in people without diabetes.',
  },
];

const PLATFORM_TRANSLATIONS = [
  {
    icon: Rows3,
    title: 'One client view',
    text:
      'Bring nutrition intake, relevant biomarkers, client-provided medications, lifestyle patterns and goals into a single workspace.',
  },
  {
    icon: ClipboardList,
    title: 'Structured practitioner workflow',
    text:
      'Turn assessment data into food and lifestyle strategies shaped around clinical priorities, risk factors and readiness for change.',
  },
  {
    icon: ShieldCheck,
    title: 'Clinical control stays with you',
    text:
      'Use AI-assisted tools to move faster while reviewing, editing and approving the plan before it reaches the client.',
  },
];

const SCIENCE_PILLARS = [
  {
    icon: FlaskConical,
    title: 'Trial-backed dietary improvement',
    text:
      'Randomised controlled trials such as Food4Me showed personalised nutrition advice delivered digitally can improve diet quality beyond generalised advice.',
  },
  {
    icon: Microscope,
    title: 'Multiple data streams matter',
    text:
      'Recent reviews highlight how combining diet, phenotype, microbiota and genetics with decision support can enable more precise interventions.',
  },
  {
    icon: ScanSearch,
    title: 'Designed for applied practice',
    text:
      'CalorieScience is built around evidence-based personalisation, data integration and practitioner-guided behaviour change rather than quick-fix meal plans.',
  },
];

const INPUT_CATEGORIES = [
  {
    icon: Activity,
    eyebrow: 'Clinical markers',
    title: 'Biomarkers and phenotype',
    intro: 'Objective measures help anchor nutrition planning around risk, response and progress.',
    items: [
      'Glycaemic markers (e.g. HbA1c, fasting glucose)',
      'Lipids and cardiometabolic risk markers',
      'Weight, body composition and waist measures',
    ],
  },
  {
    icon: Dna,
    eyebrow: 'Emerging signals',
    title: 'Genetics, microbiota and "omics" (where available)',
    intro: 'These inputs can add nuance, but they work best when interpreted inside the wider clinical picture.',
    items: [
      'Nutrigenetic insights can indicate potential variations in nutrient metabolism or sensitivity, though they should be interpreted cautiously and in context.',
      'Microbiome-based services are increasingly used to generate personalised dietary suggestions, but current guidelines emphasise that they complement, not replace, clinical judgement.',
    ],
  },
  {
    icon: Brain,
    eyebrow: 'Lived context',
    title: 'Lifestyle, environment and preferences',
    intro: 'The most clinically elegant plan still fails if it does not fit how the client actually lives.',
    items: [
      'Sleep, stress, activity and work patterns, all of which shape appetite, food choices and metabolic responses.',
      'Dietary preferences, cultural context, ethical choices and practical constraints, as highlighted by UK nutrition organisations promoting person-centred care.',
    ],
  },
];

const WHY_CHOOSE_ITEMS = [
  'AI-assisted nutrition workflows',
  'Biomarker-informed nutrition planning',
  'Meal plans generated in minutes, not the 30–60 minutes manual planning typically takes',
  'Structured client onboarding',
  'Adherence monitoring',
  'Practice management tools',
  'Less time on admin, more time in consultations',
  'Designed for scalable nutrition care',
];

const WORKFLOW_STEPS = [
  {
    title: '1 · Ingest and unify data',
    text: 'The platform pulls together lab results, lifestyle questionnaires, food logs, activity data, and (where available) genetic or microbiome inputs into a single client profile.',
  },
  {
    title: '2 · Generate a personalised meal plan',
    text: 'Drawing on that profile and the practitioner’s clinical priorities, the AI generates a meal plan optimised across nutritional targets, preferences, cultural context, and practical constraints — simultaneously, in seconds, versus the 30–60 minutes this typically takes to build by hand.',
  },
  {
    title: '3 · Surface for practitioner review',
    text: 'All AI-generated recommendations are presented to the practitioner for clinical review, annotation, and override before delivery. The platform handles computation; the clinician retains judgement.',
  },
  {
    title: '4 · Continuous adaptation',
    text: 'As clients log meals and share progress data, the system refines recommendations over time — flagging anomalies or changes in response patterns for practitioner attention.',
  },
  {
    title: '5 · Deliver to the client',
    text: 'Final plans reach clients through a branded interface including shopping lists, recipe guidance, and progress tracking — designed to support adherence over the long term.',
  },
];

const AI_SUPPORT_ITEMS = [
  'Highlighting dietary patterns associated with reported symptoms or biometric trends, based on emerging evidence.',
  "Suggesting meal templates that match a client's nutritional targets and preferences, for you to review and adjust.",
  'Automating repetitive tasks such as converting diaries into analysable data, so you can spend more time in consultation and behaviour change work, which BANT and other UK bodies emphasise as critical for sustainable outcomes.',
];

const JOURNEY_ITEMS = [
  'A BANT-registered nutritional therapist in Manchester works with a client who has fluctuating energy, weight concerns and borderline glycaemic markers reported by their GP.',
  "Using CalorieScience, the practitioner imports the client's food diary, logs relevant biomarkers, and records sleep and stress patterns from a wearable app, creating a complete profile.",
  'The platform highlights frequent late-night high-glycaemic snacks and irregular breakfast patterns associated with larger post-meal glucose swings in similar contexts described in recent PPGR studies.',
  'The practitioner designs a phased, personalised plan focused on adjusting carbohydrate quality and timing, adding protein- and fibre-rich options, and reducing discretionary foods, consistent with evidence from personalised nutrition trials.',
  'Follow-up reviews track changes in food patterns, symptoms and relevant markers, allowing the plan to be refined over time.',
];

const COMPARISON_ROWS = [
  {
    label: 'Core focus',
    traditional: 'Nutrient calculation, menu/label output',
    practice: 'Scheduling, billing, client records',
    cs: 'Biomarker-informed, personalised care',
  },
  {
    label: 'Meal plans',
    traditional: 'Population-level, template-based',
    practice: 'Manual, practitioner-built',
    cs: 'AI-generated from client data, practitioner-reviewed',
  },
  {
    label: 'Biomarker integration',
    traditional: 'Not typically included',
    practice: 'Not typically included',
    cs: 'Built in — blood, vitamin, CGM data',
  },
  {
    label: 'Individual-level longitudinal record',
    traditional: 'Limited',
    practice: 'Yes',
    cs: 'Yes, combined with biomarker trends',
  },
  {
    label: 'AI-assisted interpretation',
    traditional: 'No',
    practice: 'No',
    cs: 'Yes, with full practitioner override',
  },
  {
    label: 'UK/EU GDPR compliance',
    traditional: 'Varies by provider',
    practice: 'Varies by provider',
    cs: 'UK and EU GDPR compliant (HIPAA in progress)',
  },
];

const HeroVisual: React.FC = () => {
  return (
    <div className={styles.heroVisual}>
      <MarketingImage src={mealPlanScreen} alt="CalorieScience personalised meal planning workspace showing an AI-generated plan awaiting practitioner review" className={styles.heroVisualImage} loading="lazy" />
      <div className={styles.heroVisualBadge} aria-hidden="true">
        <MarketingImage src={brandMarkLogo} alt="" />
      </div>
    </div>
  );
};

const FaqAccordion: React.FC = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className={styles.faqList}>
      {PNP_FAQS.map((faq, index) => {
        const isOpen = openIndex === index;
        return (
          <article className={styles.faqItem} key={faq.question}>
            <button
              type="button"
              className={styles.faqQuestion}
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              aria-expanded={isOpen}
            >
              <span>{faq.question}</span>
              <span className={styles.faqIcon} aria-hidden="true">
                {isOpen ? <ChevronUp /> : <ChevronDown />}
              </span>
            </button>
            {isOpen ? <p>{faq.answer}</p> : null}
          </article>
        );
      })}
    </div>
  );
};

const PersonalisedNutritionPage: React.FC = () => {
  usePnpPageSeo();

  return (
    <div className={styles.page}>
      <div className={styles.heroBackdrop}>
        <Navbar />
        <section className={styles.hero}>
          <div className={styles.inner}>
            <p className={styles.breadcrumb}>Home&nbsp;/&nbsp;Personalised Nutrition Platform</p>
            <div className={styles.heroInner}>
              <div>
                <div className={styles.trustBadges}>
                  <span><ShieldCheck size={13} aria-hidden="true" /> UK &amp; EU GDPR Compliant (HIPAA in progress)</span>
                  <span><Sparkles size={13} aria-hidden="true" /> AI-Assisted</span>
                  <span><Stethoscope size={13} aria-hidden="true" /> Built for UK Practitioners</span>
                </div>
                <h1 className={styles.heroTitle}>Personalised Nutrition Platform</h1>
                <p className={styles.heroSubtitle}>
                  Personalised nutrition platform technology is changing how practitioners deliver nutrition care.
                  Instead of relying on generic recommendations, practitioners can now combine biomarkers, dietary
                  preferences, food data, and clinical insights to create nutrition plans tailored to the individual.
                </p>
                <ul className={styles.valueProps}>
                  <li><CheckCircle2 size={16} aria-hidden="true" /> Connect biomarkers, genetics and lifestyle data in one client view</li>
                  <li><CheckCircle2 size={16} aria-hidden="true" /> Generate a personalised meal plan in minutes, not the 30–60 minutes manual planning typically takes</li>
                  <li><CheckCircle2 size={16} aria-hidden="true" /> Stay in full clinical control, every AI suggestion is reviewable and editable</li>
                  <li><CheckCircle2 size={16} aria-hidden="true" /> Built specifically for UK dietitians, nutritionists and nutritional therapists</li>
                </ul>
                <div className={styles.heroCtas}>
                  <a href="/signup" className={styles.primaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="pnp_hero_get_started" data-analytics-section="personalised_nutrition_platform">
                    Get Started For Free
                  </a>
                  <a
                    href={PNP_DEMO_VIDEO_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.secondaryButton}
                    data-analytics-event="Landing CTA Clicked"
                    data-analytics-id="pnp_hero_watch_demo"
                    data-analytics-section="personalised_nutrition_platform"
                  >
                    <PlayCircle size={16} aria-hidden="true" /> Watch a 2-Minute Demo
                  </a>
                </div>
                <p className={styles.reassurance}>
                  31-day free trial &middot; No credit card required &middot; Cancel anytime &middot; Setup in minutes
                </p>
                <p className={styles.byline}>
                  Reviewed by <Link to="/#mission">{PNP_REVIEWED_BY}</Link> &middot; Last updated {PNP_LAST_REVIEWED_DISPLAY}
                </p>
              </div>
              <HeroVisual />
            </div>
          </div>
        </section>
      </div>

      <main className={styles.main}>
        {/* Stat grid */}
        <section className={styles.section} id="evidence">
          <div className={styles.inner}>
            <div className={styles.statGrid}>
              {STAT_CARDS.map(card => (
                <a
                  key={card.stat}
                  href={card.href}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.statCard}
                >
                  <p className={styles.statHeadline}>{card.stat}</p>
                  <p className={styles.statText}>{card.text}</p>
                  <span className={styles.statSource}>
                    {card.source} <ExternalLink size={12} aria-hidden="true" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Why generic plans fail */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="why-generic-plans-fail">
          <div className={styles.inner}>
            <div className={styles.insightLayout}>
              <div className={styles.insightIntro}>
                <span className={styles.kicker}>The gap in generic plans</span>
                <h2 className={styles.sectionTitle}>Why generic diet plans fail your clients</h2>
                <p className={styles.sectionText}>
                  Two people can follow the same &ldquo;healthy&rdquo; plan and get very different results. The issue is
                  not effort; it is that biology, routine, preferences and response patterns rarely fit a static
                  template.
                </p>
                <div className={styles.insightCallout}>
                  <CircleAlert size={20} aria-hidden="true" />
                  <p>
                    A plan that ignores individual context quickly becomes hard to follow, hard to explain and hard
                    to adapt.
                  </p>
                </div>
              </div>
              <div className={styles.insightCards}>
                {GENERIC_PLAN_INSIGHTS.map(({ icon: Icon, title, text }) => (
                  <article className={styles.insightCard} key={title}>
                    <span className={styles.insightIcon} aria-hidden="true">
                      <Icon size={21} />
                    </span>
                    <div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What personalised nutrition means */}
        <section className={styles.section} id="what-it-means">
          <div className={styles.inner}>
            <div className={styles.practiceLayout}>
              <div className={styles.practiceIntro}>
                <span className={styles.kicker}>Personalisation in practice</span>
                <h2 className={styles.sectionTitle}>What personalised nutrition means in practice</h2>
                <p className={styles.sectionText}>
                  Personalised nutrition puts the individual&apos;s biology, history and lived experience at the
                  centre of care, instead of fitting them into a generic protocol.
                </p>
                <p className={styles.sectionText}>
                  UK bodies such as <a href="https://bant.org.uk/" target="_blank" rel="noreferrer">BANT</a> describe
                  personalised nutrition as tailoring recommendations based on symptom patterns, clinical markers,
                  genetics, microbiome, lifestyle and preferences within a structured, evidence-based framework.
                </p>
              </div>
              <div className={styles.platformPanel}>
                <p className={styles.translatesLabel}>On CalorieScience, that means:</p>
                {PLATFORM_TRANSLATIONS.map(({ icon: Icon, title, text }) => (
                  <div className={styles.platformRow} key={title}>
                    <span className={styles.platformIcon} aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Science */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="the-science">
          <div className={styles.inner}>
            <div className={styles.scienceLayout}>
              <div className={styles.scienceIntro}>
                <span className={styles.kicker}>Evidence base</span>
                <h2 className={styles.sectionTitle}>The science behind personalised nutrition</h2>
                <p className={styles.sectionText}>
                  The evidence base for personalised nutrition has grown rapidly over the last decade, especially in
                  digital delivery models that combine assessment data with practitioner-led advice.
                </p>
                <div className={styles.scienceReference}>
                  <BookOpenText size={18} aria-hidden="true" />
                  <p>
                    The current direction of the literature points toward more precise, behaviour-change-oriented
                    interventions when individual context is actually used in the plan.
                  </p>
                </div>
              </div>
              <div className={styles.scienceCards}>
                {SCIENCE_PILLARS.map(({ icon: Icon, title, text }) => (
                  <article className={styles.scienceCard} key={title}>
                    <span className={styles.scienceIcon} aria-hidden="true">
                      <Icon size={21} />
                    </span>
                    <div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How CalorieScience supports planning */}
        <section className={styles.section} id="how-we-support-planning">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>How CalorieScience supports personalised meal planning</h2>
            </div>

            <div className={styles.subBlock}>
              <h3><Database size={18} aria-hidden="true" /> Unified client nutrition data</h3>
              <p>Bring together the information you already collect into one secure workspace:</p>
              <ul className={styles.bulletList}>
                <li>Food diaries and 24-hour recalls</li>
                <li>Symptom timelines, health history and medications as reported by the client</li>
                <li>Anthropometrics and relevant lab markers where available (for example, HbA1c, lipids, inflammatory markers)</li>
                <li>Activity, sleep and stress indicators from integrated apps or client-reported logs</li>
              </ul>
              <p>
                This mirrors how UK Registered Nutritional Therapy Practitioners are encouraged to assess
                nutritional imbalances using a whole-person view, rather than isolated metrics.
              </p>
            </div>

            <div className={styles.subBlock}>
              <h3><Activity size={18} aria-hidden="true" /> Evidence-informed nutrition analysis</h3>
              <p>
                Traditional nutrition analysis software focuses on macronutrients and vitamin/mineral targets, which
                is essential but often insufficient for nuanced cases. CalorieScience builds on this foundation by
                helping you:
              </p>
              <ul className={styles.bulletList}>
                <li>Analyse diet quality in the context of your chosen guidelines and reference values.</li>
                <li>
                  Flag patterns associated with glycaemic variability, ultra-processed food intake and other
                  factors linked to cardiometabolic risk, based on the emerging personalised nutrition literature.
                </li>
              </ul>
            </div>

            <div className={styles.subBlock}>
              <h3><Salad size={18} aria-hidden="true" /> Personalised meal planning, not just calorie counting</h3>
              <p>Using your assessment, you can generate and refine meal plans that:</p>
              <ul className={styles.bulletList}>
                <li>
                  Respect clinical constraints (for example, carbohydrate distribution, sodium limits, texture
                  modifications) where appropriate to the client&apos;s care plan.
                </li>
                <li>
                  Reflect cultural background, food access, budget and preferences, which are key determinants of
                  long-term adherence.
                </li>
                <li>
                  Include practical swaps and &ldquo;nudges&rdquo; to reduce discretionary foods and support
                  incremental change, echoing findings that personalised nutrition can help lower intake of
                  high-fat, high-sugar and high-salt foods.
                </li>
              </ul>
              <p>
                You remain in full control of the plan; the software simply makes it faster and more consistent to
                translate complex data into real-world menus.
              </p>
            </div>
          </div>
        </section>

        {/* Key inputs */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="key-inputs">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Key inputs: biomarkers, genetics, lifestyle and preferences</h2>
              <p className={styles.sectionText}>
                A personalised meal planning approach is only as strong as the data informing it. The most
                clinically meaningful inputs fall into four categories, and the combination matters as much as the
                individual components.
              </p>
            </div>
            <div className={styles.inputGrid}>
              {INPUT_CATEGORIES.map(({ icon: Icon, eyebrow, title, intro, items }) => (
                <div className={styles.inputCard} key={title}>
                  <div className={styles.inputCardTop}>
                    <span className={styles.inputEyebrow}>{eyebrow}</span>
                    <span className={styles.inputIcon} aria-hidden="true">
                      <Icon size={20} />
                    </span>
                  </div>
                  <h3>{title}</h3>
                  <p className={styles.inputIntro}>{intro}</p>
                  <p className={styles.inputListLabel}>What this includes</p>
                  <ul>
                    {items.map(item => (
                      <li key={item}>
                        <span className={styles.inputListIcon} aria-hidden="true">
                          <Check size={14} />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className={styles.sectionText} style={{ maxWidth: '48rem', margin: '1.75rem auto 0' }}>
              Each input layer interacts with the others. A client&apos;s genetic predisposition toward slower
              saturated fat clearance may be partially offset by a diverse, plant-rich diet and active lifestyle.
              Their theoretically optimal macronutrient ratio may need substantial adjustment to fit around shift
              work or family cooking routines. A personalised nutrition platform holds this complexity
              simultaneously, which no practitioner can do manually at scale across a large caseload.
            </p>
            <p className={styles.gdprNote}>
              GDPR and UK data governance: any platform used to process biomarker, genetic, or health data for UK
              clients must comply with UK GDPR (retained from EU GDPR post-Brexit) and the Data Protection Act 2018.
              Genetic data is classified as special category data under UK GDPR and requires explicit consent and
              appropriate safeguards. Practitioners should confirm compliance with their platform provider before
              collecting this data.
            </p>
            <p className={styles.sectionText} style={{ maxWidth: '48rem', margin: '1rem auto 0' }}>
              CalorieScience is a <b>UK and EU GDPR compliant (HIPAA in progress)</b> solution that gives you a
              structured way to record, review and use these inputs without overwhelming your workflow.
            </p>
          </div>
        </section>

        {/* AI at scale */}
        <section className={styles.section} id="ai-at-scale">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>How AI enables personalisation at scale</h2>
              <p className={styles.sectionText}>
                The science underpinning personalised nutrition has been understood for some years. The barrier to
                widespread clinical adoption has been operational: a practitioner with 50 or 100 clients cannot
                manually cross-reference biomarker data, gut microbiome analysis, genetic inputs, lifestyle
                constraints, and food preferences to generate, adapt, and update a genuinely tailored plan for each
                person every few weeks. It requires software.
              </p>
              <p className={styles.sectionText}>
                AI-assisted personalised nutrition platforms address exactly this gap. The Weizmann Institute study
                demonstrated that a machine learning algorithm integrating multiple individual data streams could
                accurately predict personalised glycaemic responses to real-life meals, and that a dietary
                intervention based on these predictions produced measurably better outcomes than standard advice.
              </p>
            </div>

            <div className={styles.subBlock}>
              <h3>Personalised meal planning without the administrative burden</h3>
              <p>Meal planning remains one of the most time-intensive parts of nutrition practice.</p>
              <p>
                Practitioners often balance: clinical requirements, nutrient targets, preferences, health
                conditions, cultural needs, and adherence challenges. Doing this manually for every client can be
                difficult to scale.
              </p>
              <p>
                CalorieScience helps practitioners generate personalised meal plans using structured client
                information, biomarker insights and practitioner-defined parameters, typically in minutes, compared
                with the 30–60 minutes many practitioners spend building a fully personalised plan by hand. The
                result is more time for consultations and less time spent rebuilding plans from scratch.
              </p>
            </div>

            <div className={styles.subBlock}>
              <h3>The missing piece: nutrition adherence</h3>
              <p>A personalised plan only creates value when it is followed.</p>
              <p>
                Many practitioners identify adherence, not plan creation, as the biggest challenge in nutrition
                care. Common barriers include forgetfulness, complexity, lifestyle constraints, lack of follow-up,
                and poor visibility into progress.
              </p>
              <p>
                Improving adherence requires structured monitoring, clear communication, behavioural support, and
                ongoing engagement. This is why modern nutrition platforms increasingly focus on both planning and
                implementation.
              </p>
            </div>

            <div className={styles.subBlock}>
              <h3>A practitioner-centred approach to personalised nutrition</h3>
              <p>The future of nutrition practice is not about generating more data. It is about making data actionable.</p>
              <p>
                Practitioners need systems that help them understand clients faster, connect biomarkers to nutrition
                recommendations, deliver personalised meal plans, monitor adherence, and scale care without
                compromising quality.
              </p>
              <p>
                <Link to="/">CalorieScience</Link> was built around this principle: bringing structure to
                personalised nutrition so practitioners can focus on the work that matters most.
              </p>
            </div>

            <div className={styles.subBlock}>
              <h3>Why practitioners choose CalorieScience</h3>
              <ul className={styles.checkGrid}>
                {WHY_CHOOSE_ITEMS.map(item => (
                  <li key={item}>
                    <CheckCircle2 size={16} aria-hidden="true" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Workflow diagram */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="how-it-works">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>How it works: from data to delivered plan</h2>
            </div>
            <div className={styles.workflowImageWrap}>
              <MarketingImage
                src={PNP_WORKFLOW_IMAGE_DESKTOP}
                alt="Five-step CalorieScience workflow: data ingestion and unification, personalised meal plan generation, practitioner review and override, continuous adaptation, and client delivery"
                className={styles.workflowImageDesktop}
                loading="lazy"
              />
              <MarketingImage
                src={PNP_WORKFLOW_IMAGE_MOBILE}
                alt="Five-step CalorieScience workflow: ingest and unify data, generate meal plan, practitioner review, continuous adaptation, deliver to client"
                className={styles.workflowImageMobile}
                loading="lazy"
              />
            </div>
            <div className={styles.workflowSteps}>
              {WORKFLOW_STEPS.map(step => (
                <div className={styles.workflowStep} key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI supports not replaces */}
        <section className={styles.section} id="ai-supports">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>On CalorieScience, AI is used to support, not replace, your expertise by:</h2>
            </div>
            <ul className={styles.bulletList} style={{ maxWidth: '46rem', margin: '0 auto' }}>
              {AI_SUPPORT_ITEMS.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className={styles.sectionText} style={{ maxWidth: '46rem', margin: '1.25rem auto 0', textAlign: 'center' }}>
              You decide which suggestions to accept; every recommendation is transparent and editable.
            </p>
          </div>
        </section>

        {/* Illustrative journey */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="practitioner-journey">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Example practitioner journey (illustrative scenario)</h2>
              <p className={styles.sectionText}>
                To show how this can work in real life, imagine a composite scenario based on common use cases seen
                in the literature and UK practice, not an individual case or guaranteed outcome.
              </p>
            </div>
            <ul className={styles.bulletList} style={{ maxWidth: '46rem', margin: '0 auto' }}>
              {JOURNEY_ITEMS.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className={styles.disclaimerNote}>
              Again, this is an example of how the workflow can look, not a promise of specific clinical results.
            </p>
          </div>
        </section>

        {/* Comparison table */}
        <section className={styles.section} id="comparison">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>CalorieScience vs traditional nutrition software</h2>
              <p className={styles.sectionText}>
                Most nutrition software used in the UK has historically been built for nutrient analysis, menu
                management and compliance reporting, for example in healthcare catering, education and sports
                settings. Practice-management platforms like Practice Better, Healthie and NutriAdmin, and
                nutrition-analysis tools like Nutrium, cover scheduling, billing and standard meal-plan building
                well.
              </p>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.comparisonTable}>
                <thead>
                  <tr>
                    <th />
                    <th>Traditional nutrition analysis software</th>
                    <th>General practice-management platforms</th>
                    <th>CalorieScience</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map(row => (
                    <tr key={row.label}>
                      <td className={styles.rowLabel}>{row.label}</td>
                      <td>{row.traditional}</td>
                      <td>{row.practice}</td>
                      <td className={styles.csCell}>{row.cs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.sectionText} style={{ maxWidth: '48rem', margin: '1.75rem auto 0' }}>
              You can still perform core analysis tasks on CalorieScience, but the platform is optimised for
              one-to-one personalisation grounded in biomarker data, not only population-level menu design or
              general practice admin.
            </p>
          </div>
        </section>

        {/* Why choose / who for */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="who-its-for">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Why choose CalorieScience?</h2>
              <p className={styles.sectionText}>
                It&apos;s the only piece of software in this space built around a single idea: personalised
                nutrition backed by real biomarker data, without adding hours of manual work. AI handles the
                data-crunching; you keep every clinical decision.
              </p>
            </div>
            <div className={styles.subBlock}>
              <h3><Users size={18} aria-hidden="true" /> Who CalorieScience is for?</h3>
              <p>CalorieScience is built for UK-based professionals who want to integrate personalised nutrition into everyday practice:</p>
              <ul className={styles.bulletList}>
                <li>Registered dietitians and nutritionists working in clinics, community settings or private practice.</li>
                <li>Nutritional therapy practitioners and functional medicine professionals who use detailed case-taking and testing as part of their work.</li>
                <li>Health coaches and multidisciplinary teams who need a structured way to align food, lifestyle and behaviour support with medical care plans.</li>
              </ul>
              <p className={styles.disclaimerNote}>
                The platform is a professional tool and is not intended to replace medical advice, diagnosis or
                treatment. Clients should always be advised to consult their GP or specialist regarding medical
                conditions and medication changes.
              </p>
            </div>
            <div className={styles.subBlock}>
              <h3>What happens after signup?</h3>
              <p>
                Create your practitioner account, add your first client&apos;s intake and any available biomarker
                data, and generate your first AI-assisted, practitioner-reviewed meal plan, typically within your
                first session.
              </p>
            </div>
          </div>
        </section>

        {/* Post CTA */}
        <section className={styles.section} id="post-cta">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                See CalorieScience <em>in action</em>
              </h2>
              <p className={styles.sectionText}>The AI-assisted, UK/EU GDPR-compliant nutrition platform built for practitioners.</p>
            </div>
            <div className={styles.heroCtas} style={{ justifyContent: 'center' }}>
              <a href="/signup" className={styles.primaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="pnp_post_cta_trial" data-analytics-section="personalised_nutrition_platform">
                Start free trial
              </a>
              <a
                href="mailto:info@caloriescience.ai"
                className={styles.secondaryButton}
                data-analytics-event="Landing CTA Clicked"
                data-analytics-id="pnp_post_cta_talk"
                data-analytics-section="personalised_nutrition_platform"
              >
                Talk to us
              </a>
            </div>
            <p className={styles.disclosure}>
              CalorieScience is a workflow and education platform that supports practitioner judgement. It is UK and
              EU GDPR compliant; HIPAA is in progress. It is not a diagnostic tool, not a medical device, and does
              not replace clinical assessment by a qualified practitioner.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="faq">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>FAQ</span>
              <h2 className={styles.sectionTitle}>FAQs on personalised nutrition and CalorieScience</h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default PersonalisedNutritionPage;
